import React from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "./Header";
import SeoHead from "./SeoHead";
import { ChevronRight } from "lucide-react";

/**
 * Shared layout for all /practice/* pages. Left column holds the content, the
 * right rail is reserved for a future ad slot (kept narrow so it never
 * overlaps the practice content) and a "Practice menu" quick-jump list.
 *
 * The right rail is hidden on mobile so nothing competes with the widget UI.
 */
const LEVELS = [
  { to: "/practice/level/basic", label: "Level 1 · Basic" },
  { to: "/practice/level/forms", label: "Level 2 · Forms" },
  { to: "/practice/level/mouse-keyboard", label: "Level 3 · Mouse & Keyboard" },
  { to: "/practice/level/complex", label: "Level 4 · Complex Components" },
  { to: "/practice/level/dynamic", label: "Level 5 · Dynamic UI" },
  { to: "/practice/level/browser", label: "Level 6 · Browser Handling" },
  { to: "/practice/level/advanced", label: "Level 7 · Advanced Automation" },
  { to: "/practice/api", label: "API Testing Playground" },
];

export default function PracticeLayout({ eyebrow, title, subtitle, seo, children }) {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-zinc-50">
      {seo && (
        <SeoHead
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          image={seo.image}
          path={seo.path || pathname}
        />
      )}
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="text-xs tag-mono text-zinc-500 mb-6 flex items-center gap-1.5" data-testid="practice-breadcrumb">
          <Link to="/" className="hover:text-zinc-900">home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/practice" className="hover:text-zinc-900">practice</Link>
          {pathname !== "/practice" && (
            <>
              <ChevronRight className="h-3 w-3" />
              <span className="text-zinc-900">{eyebrow?.toLowerCase() || "page"}</span>
            </>
          )}
        </nav>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_260px] gap-8">
          {/* Content column */}
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1" data-testid="practice-eyebrow">
                {eyebrow}
              </div>
            )}
            {title && (
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950 mb-2">
                {title}
              </h1>
            )}
            {subtitle && <p className="text-zinc-600 mb-8 max-w-2xl">{subtitle}</p>}
            {children}
          </div>

          {/* Right rail — reserved for ads later + quick jump */}
          <aside className="hidden lg:block" data-testid="practice-right-rail">
            <div className="sticky top-24 space-y-4">
              <div
                className="border border-dashed border-zinc-300 rounded-md p-4 text-center text-xs tag-mono text-zinc-400 bg-white h-[250px] grid place-items-center"
                data-testid="practice-ad-slot"
                aria-label="Sponsored slot — reserved"
              >
                <span>Sponsored slot<br />(reserved · 250×250)</span>
              </div>
              <div className="bg-white border border-zinc-200 rounded-md p-4">
                <div className="text-[10px] tag-mono uppercase tracking-widest text-zinc-500 font-bold mb-2">
                  practice menu
                </div>
                <nav className="flex flex-col text-sm">
                  {LEVELS.map((l) => {
                    const active = pathname === l.to;
                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        data-testid={`practice-menu-${l.to.split("/").pop()}`}
                        className={`px-2 py-1.5 rounded transition-colors ${
                          active ? "bg-blue-50 text-[#002FA7] font-semibold" : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-950"
                        }`}
                      >
                        {l.label}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
